import { NationalityType, occupationList } from '@/models/self-checkin/accommodation-info'
import { SelfCheckInState } from '@/models/self-checkin'
import { JapaneseIdentityDocumentType } from '@/models/self-checkin/identification'

export const getTranslateOccupation = value => {
  const occupation = occupationList.find(i => i.value === value)

  return occupation?.name
}

export const IdentificationRequirement = {
  None: 'none',
  License: 'license',
  Passport: 'passport',
} as const
export type IdentificationRequirement = (typeof IdentificationRequirement)[keyof typeof IdentificationRequirement]

/**
 * 現地で提出を求める本人確認書類を返す。
 *
 * 事前チェックインで本人確認が登録済み (hasIdentification) の場合は、施設設定に関わらず求めない。
 * バックエンド未反映の間は hasIdentification が undefined になるため、従来どおり施設設定のみで判断される。
 */
export const getIdentificationRequirement = (state: SelfCheckInState, nationality: string | undefined): IdentificationRequirement => {
  if (state.selectReservation?.hasIdentification) {
    return IdentificationRequirement.None
  }
  if (nationality !== NationalityType.Jp) {
    return state.setting.accommodation.requiredForeignIdentify ? IdentificationRequirement.Passport : IdentificationRequirement.None
  }
  return state.setting.accommodation.requiredJapaneseIdentify ? IdentificationRequirement.License : IdentificationRequirement.None
}

type IdentityDocumentSide = 'front' | 'back'

// 登録名は事前チェックイン（aipass_guest の japanese-resident-form.tsx）と揃え、どちらで登録しても同じ名前になるようにしている
const RequiredSidesByJapaneseIdentityDocumentType: Record<
  JapaneseIdentityDocumentType,
  Array<{ side: IdentityDocumentSide; registerName: string }>
> = {
  [JapaneseIdentityDocumentType.DriversLicense]: [
    { side: 'front', registerName: '免許証表面' },
    { side: 'back', registerName: '免許証裏面' },
  ],
  [JapaneseIdentityDocumentType.MyNumberCard]: [{ side: 'front', registerName: 'マイナンバーカード表面' }],
  [JapaneseIdentityDocumentType.ResidenceCard]: [
    { side: 'front', registerName: '在留カード表面' },
    { side: 'back', registerName: '在留カード裏面' },
  ],
}

type JapaneseIdentityDocumentImages = Pick<
  NonNullable<SelfCheckInState['uploadLicense']>,
  'documentType' | 'identificationFrontImage' | 'identificationFrontObjectKey' | 'identificationBackImage' | 'identificationBackObjectKey'
>

// 種類を選ぶ前は運転免許証（表・裏）しか受け付けていなかったため、種類を持たない state は運転免許証として扱う
const requiredSidesOf = (documentType: JapaneseIdentityDocumentType | undefined) =>
  RequiredSidesByJapaneseIdentityDocumentType[documentType ?? JapaneseIdentityDocumentType.DriversLicense]

const imageOf = (images: JapaneseIdentityDocumentImages, side: IdentityDocumentSide) =>
  side === 'front'
    ? { image: images.identificationFrontImage, objectKey: images.identificationFrontObjectKey }
    : { image: images.identificationBackImage, objectKey: images.identificationBackObjectKey }

// 表はどの種類でも撮るため、画面は表を常に出し、裏だけをこの判定で出し分ける
export const isJapaneseIdentityDocumentBackRequired = (documentType: JapaneseIdentityDocumentType | undefined): boolean =>
  requiredSidesOf(documentType).some(({ side }) => side === 'back')

export const hasRequiredJapaneseIdentityDocumentImages = (images: JapaneseIdentityDocumentImages = {}): boolean =>
  requiredSidesOf(images.documentType).every(({ side }) => !!imageOf(images, side).image)

export const buildJapaneseIdentityDocumentImages = (images: JapaneseIdentityDocumentImages) =>
  requiredSidesOf(images.documentType).map(({ side, registerName }) => ({
    // 事前チェックイン（aipass_guest）に揃え、パスポート以外は種類を問わず 1 で登録する
    isDriversLicense: 1,
    objectKey: imageOf(images, side).objectKey,
    name: registerName,
  }))
